/**
 * Signup Page
 * User registration with role selection
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { Input, Button } from '../../components';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/common/SEO';

const Signup = () => {
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student',
    agreeTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setError('');
      try {
        const user = await loginWithGoogle(tokenResponse.access_token, formData.role);
        navigate(`/${user.role}/dashboard`);
      } catch (err) {
        setError(err.message || 'Google sign-up failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setError('Google sign-up was cancelled or failed. Please try again.');
    },
  });

  const handleGoogleSignUp = () => {
    setError('');
    googleLogin();
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return false;
    }
    if (!formData.agreeTerms) {
      setError('Please agree to the terms and conditions.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => { 
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    try {
      await signup(formData.name, formData.email, formData.password, formData.role);
      navigate(`/${formData.role}/dashboard`);
      return;
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lh-signup-page min-h-screen bg-gradient-to-br from-dark-50 via-white to-primary-50 flex items-center justify-center p-4">
      <SEO
        title="Sign Up"
        description="Create your free Lotus Arc Technologies account and start learning React, Java, Python, AI, and more with expert-led live classes."
        canonicalPath="/signup"
        keywords={['sign up', 'create account', 'free registration', 'start learning']}
      />
      <div className="lh-signup__container w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="lh-signup__logo flex items-center justify-center gap-2 mb-8">
          <img src="/lat-icon.png" alt="Lotus Arc Technologies" className="h-14 w-auto" />
          <span className="text-2xl font-bold text-dark-900">LAT</span>
        </Link>

        {/* Signup Card */}
        <div className="lh-signup__card bg-white rounded-2xl shadow-xl p-8">
          <div className="lh-signup__header text-center mb-6">
            <h1 className="lh-signup__title text-2xl font-bold text-dark-900 mb-2">
              Create Your Account
            </h1>
            <p className="lh-signup__subtitle text-dark-500">
              Start your learning journey today
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="lh-signup__error bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign-Up */}
          <button
            onClick={handleGoogleSignUp}
            disabled={loading || googleLoading}
            className="lh-signup__google-btn w-full flex items-center justify-center gap-3 bg-white border-2 border-dark-200 hover:border-dark-300 text-dark-700 font-medium py-3 px-4 rounded-lg transition-colors mb-6 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-dark-300 border-t-primary-500 rounded-full animate-spin" />
            ) : (
              <svg className="lh-signup__google-icon w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            <span className="lh-signup__google-text">
              {googleLoading ? 'Signing up...' : 'Sign up with Google'}
            </span>
          </button>

          {/* Divider */}
          <div className="lh-signup__divider flex items-center gap-4 mb-6">
            <div className="lh-signup__divider-line flex-1 h-px bg-dark-200" />
            <span className="lh-signup__divider-text text-sm text-dark-400">or continue with email</span>
            <div className="lh-signup__divider-line flex-1 h-px bg-dark-200" />
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="lh-signup__form space-y-4">
            <Input
              label="Full Name"
              type="text"
              name="name"
              placeholder="Enter Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="lh-signup__input lh-signup__input--name"
              icon={
                <svg className="lh-signup__input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="Enter Your Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="lh-signup__input lh-signup__input--email"
              icon={
                <svg className="lh-signup__input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            />

            {/* Role Selection */}
            <div className="lh-signup__role">
              <label className="lh-signup__role-label block text-sm font-medium text-dark-700 mb-2">
                I want to
              </label>
              <div className="lh-signup__role-options flex gap-4">
                <label className={`lh-signup__role-option lh-signup__role-option--student flex-1 p-4 border-2 rounded-lg cursor-pointer transition-colors ${formData.role === 'student' ? 'lh-signup__role-option--active border-primary-500 bg-primary-50' : 'border-dark-200 hover:border-dark-300'}`}>
                  <input
                    type="radio"
                    name="role"
                    value="student"
                    checked={formData.role === 'student'}
                    onChange={handleChange}
                    className="lh-signup__role-input sr-only"
                  />
                  <div className="lh-signup__role-content text-center">
                    <svg className={`lh-signup__role-icon w-8 h-8 mx-auto mb-2 ${formData.role === 'student' ? 'text-primary-500' : 'text-dark-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                    <p className={`lh-signup__role-text text-sm font-medium ${formData.role === 'student' ? 'text-primary-700' : 'text-dark-700'}`}>Learn</p>
                  </div>
                </label>
                <label className={`lh-signup__role-option lh-signup__role-option--teacher flex-1 p-4 border-2 rounded-lg cursor-pointer transition-colors ${formData.role === 'teacher' ? 'lh-signup__role-option--active border-primary-500 bg-primary-50' : 'border-dark-200 hover:border-dark-300'}`}>
                  <input
                    type="radio"
                    name="role"
                    value="teacher"
                    checked={formData.role === 'teacher'}
                    onChange={handleChange}
                    className="lh-signup__role-input sr-only"
                  />
                  <div className="lh-signup__role-content text-center">
                    <svg className={`lh-signup__role-icon w-8 h-8 mx-auto mb-2 ${formData.role === 'teacher' ? 'text-primary-500' : 'text-dark-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    <p className={`lh-signup__role-text text-sm font-medium ${formData.role === 'teacher' ? 'text-primary-700' : 'text-dark-700'}`}>Teach</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="lh-signup__password-wrapper relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                helperText="Must be at least 8 characters"
                className="lh-signup__input lh-signup__input--password"
                icon={
                  <svg className="lh-signup__input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="lh-signup__password-toggle absolute right-3 top-9 text-dark-400 hover:text-dark-600"
              >
                {showPassword ? (
                  <svg className="lh-signup__password-icon lh-signup__password-icon--hide w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="lh-signup__password-icon lh-signup__password-icon--show w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            <Input
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="lh-signup__input lh-signup__input--confirm-password"
              icon={
                <svg className="lh-signup__input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
            />

            {/* Terms Agreement */}
            <label className="lh-signup__terms flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                className="lh-signup__terms-checkbox w-4 h-4 mt-1 text-primary-500 rounded focus:ring-primary-500"
              />
              <span className="lh-signup__terms-text text-sm text-dark-600">
                I agree to the{' '}
                <Link to="/terms" className="lh-signup__terms-link text-primary-500 hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="lh-signup__terms-link text-primary-500 hover:underline">Privacy Policy</Link>
              </span>
            </label>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              className="lh-signup__submit mt-6"
            >
              Create Account
            </Button>
          </form>

          {/* Login Link */}
          <p className="lh-signup__login-link text-center mt-6 text-dark-500">
            Already have an account?{' '}
            <Link to="/login" className="lh-signup__login-link-action text-primary-500 hover:text-primary-600 font-medium">
              Sign in
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="lh-signup__back text-center mt-6">
          <Link to="/" className="lh-signup__back-link text-dark-500 hover:text-dark-700 text-sm flex items-center justify-center gap-2">
            <svg className="lh-signup__back-icon w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="lh-signup__back-text">Back to Home</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
